import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  AppBar,
  Toolbar,
  InputAdornment,
  CircularProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

const SubCategorias = () => {
  const [subCategorias, setSubCategorias] = useState([]);
  const [originalSubCategorias, setOriginalSubCategorias] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedSubCategoria, setSelectedSubCategoria] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    nombre_sub_categoria: '',
    id_pet: '',
    id_categoria: '',
    habilitada: true,
  });
  const [categorias, setCategorias] = useState([]);
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userIdPet, setUserIdPet] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const navigate = useNavigate();

  // Decodificar token JWT para obtener id_pet
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        console.log('Payload del token:', payload);
        setUserIdPet(payload.id_pet || null);
      } catch (err) {
        console.error('Error al decodificar el token:', err);
        const errorMsg = 'Error al decodificar el token de usuario';
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
      }
    } else {
      const errorMsg = 'No se encontró un token de usuario';
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  }, []);

  // Formatear fechas
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Normalizar datos de subcategoría
  const normalizeSubCategoria = (subCategoria) => {
    if (!subCategoria || !subCategoria.id_sub_categoria) {
      console.warn('SubCategoría inválida recibida:', subCategoria);
      return null;
    }
    return {
      id_sub_categoria: subCategoria.id_sub_categoria ?? null,
      nombre_sub_categoria: subCategoria.nombre_sub_categoria ?? '',
      id_pet: subCategoria.id_pet ?? null,
      id_categoria: subCategoria.id_categoria ?? null,
      habilitada: subCategoria.habilitada ?? true,
      createdAt: subCategoria.createdAt ?? null,
      updatedAt: subCategoria.updatedAt ?? null,
    };
  };

  // Obtener subcategorías, categorías y pets
  useEffect(() => {
    const fetchData = async () => {
      if (!userIdPet) {
        console.log('Esperando id_pet del usuario...');
        return;
      }
      try {
        setLoading(true);
        const subCategoriasRes = await api.get(`/sub-categorias/`);
        console.log('Respuesta de subcategorías:', subCategoriasRes.data);
        const subCategoriasData = Array.isArray(subCategoriasRes.data)
          ? subCategoriasRes.data.filter(item => item !== null && item !== undefined)
          : [];
        const normalizedSubCategorias = subCategoriasData
          .map(normalizeSubCategoria)
          .filter(subCat => subCat !== null && subCat.id_sub_categoria && subCat.id_pet === userIdPet);
        console.log('SubCategorías normalizadas:', normalizedSubCategorias);
        setSubCategorias(normalizedSubCategorias);
        setOriginalSubCategorias(normalizedSubCategorias);

        const categoriasRes = await api.get(`/categorias/?id_pet=${userIdPet}`);
        console.log('Respuesta de categorías:', categoriasRes.data);
        const categoriasData = Array.isArray(categoriasRes.data)
          ? categoriasRes.data
              .filter(item => item !== null && item !== undefined && item.id_pet === userIdPet)
              .map(cat => ({
                id_categoria: cat.id_categoria,
                nombre_categoria: cat.nombre_categoria,
                id_pet: cat.id_pet,
              }))
          : [];
        console.log('Categorías filtradas por id_pet:', categoriasData);
        setCategorias(categoriasData);

        const petsRes = await api.get('/pets/');
        console.log('Respuesta de pets:', petsRes.data);
        const petsData = Array.isArray(petsRes.data) ? petsRes.data : [];
        setPets(petsData);
        setLoading(false);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        const errorMsg = 'Error al cargar datos: ' + (error.response?.data?.detail || error.message);
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
        setLoading(false);
      }
    };
    fetchData();
  }, [userIdPet]);

  // Buscar subcategorías
  useEffect(() => {
    const searchSubCategorias = () => {
      console.log('Buscando con término:', searchTerm);
      console.log('SubCategorías originales:', originalSubCategorias);
      if (!searchTerm) {
        setSubCategorias(originalSubCategorias);
        console.log('Restaurando subcategorías:', originalSubCategorias);
        return;
      }
      const filteredSubCategorias = originalSubCategorias
        .filter(subCat => subCat && subCat.id_sub_categoria && subCat.nombre_sub_categoria)
        .filter(subCat => subCat.nombre_sub_categoria.toLowerCase().includes(searchTerm.toLowerCase()));
      console.log('SubCategorías filtradas:', filteredSubCategorias);
      setSubCategorias(filteredSubCategorias);
      setPage(0);
    };
    searchSubCategorias();
  }, [searchTerm, originalSubCategorias]);

  // Abrir modal para crear/editar subcategoría
  const handleOpenModal = (subCategoria = null) => {
    setSelectedSubCategoria(subCategoria);
    setFormData(
      subCategoria
        ? normalizeSubCategoria(subCategoria) || {
            nombre_sub_categoria: '',
            id_pet: userIdPet || '',
            id_categoria: '',
            habilitada: true,
          }
        : { nombre_sub_categoria: '', id_pet: userIdPet || '', id_categoria: '', habilitada: true }
    );
    setOpenModal(true);
  };

  // Cerrar Snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  // Guardar subcategoría (crear o editar)
  const handleSave = async () => {
    try {
      if (!formData.nombre_sub_categoria || !formData.id_pet || !formData.id_categoria) {
        const errorMsg = 'Nombre de la subcategoría, veterinaria y categoría son obligatorios';
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
        return;
      }
      if (selectedSubCategoria) {
        const response = await api.put(`/sub-categorias/${selectedSubCategoria.id_sub_categoria}`, {
          nombre_sub_categoria: formData.nombre_sub_categoria,
          id_pet: formData.id_pet,
          id_categoria: formData.id_categoria,
          habilitada: formData.habilitada,
        });
        const updatedSubCategoria = normalizeSubCategoria(response.data);
        if (updatedSubCategoria) {
          const newSubCategorias = subCategorias.map(subCat =>
            subCat.id_sub_categoria === selectedSubCategoria.id_sub_categoria ? updatedSubCategoria : subCat
          );
          setSubCategorias(newSubCategorias);
          setOriginalSubCategorias(newSubCategorias);
          setSnackbarMessage('SubCategoría actualizada exitosamente');
          setSnackbarOpen(true);
        }
      } else {
        const response = await api.post('/sub-categorias/', {
          nombre_sub_categoria: formData.nombre_sub_categoria,
          id_pet: formData.id_pet,
          id_categoria: formData.id_categoria,
          habilitada: formData.habilitada,
        });
        const newSubCategoria = normalizeSubCategoria(response.data);
        if (newSubCategoria) {
          const newSubCategorias = [...subCategorias, newSubCategoria];
          setSubCategorias(newSubCategorias);
          setOriginalSubCategorias(newSubCategorias);
          setSnackbarMessage('SubCategoría creada exitosamente');
          setSnackbarOpen(true);
        }
      }
      setOpenModal(false);
      setError(null);
      setPage(0);
    } catch (error) {
      console.error('Error al guardar subcategoría:', error);
      const errorMsg = 'Error al guardar subcategoría: ' + (error.response?.data?.detail || error.message);
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  // Deshabilitar subcategoría
  const handleToggleHabilitada = async (id, habilitada) => {
    try {
      const response = await api.put(`/sub-categorias/${id}`, { habilitada: !habilitada });
      const updatedSubCategoria = normalizeSubCategoria(response.data);
      if (updatedSubCategoria) {
        const newSubCategorias = subCategorias.map(subCat =>
          subCat.id_sub_categoria === id ? updatedSubCategoria : subCat
        );
        setSubCategorias(newSubCategorias);
        setOriginalSubCategorias(newSubCategorias);
        setSnackbarMessage(`SubCategoría ${updatedSubCategoria.habilitada ? 'habilitada' : 'deshabilitada'} exitosamente`);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error al actualizar estado:', error);
      const errorMsg = 'Error al actualizar estado: ' + (error.response?.data?.detail || error.message);
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  // Eliminar subcategoría
  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta subcategoría?')) return;
    try {
      await api.delete(`/sub-categorias/${id}`);
      const newSubCategorias = subCategorias.filter(subCat => subCat.id_sub_categoria !== id);
      setSubCategorias(newSubCategorias);
      setOriginalSubCategorias(newSubCategorias);
      setSnackbarMessage('SubCategoría eliminada exitosamente');
      setSnackbarOpen(true);
      setPage(0);
    } catch (error) {
      console.error('Error al eliminar subcategoría:', error);
      const errorMsg = 'Error al eliminar subcategoría: ' + (error.response?.data?.detail || error.message);
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  // Manejo de paginación
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const paginatedSubCategorias = subCategorias.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3 }}>
      <AppBar position="relative" sx={{ bgcolor: '#1e293b', mb: 3 }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Gestión de SubCategorías
          </Typography>
          <TextField
            variant="outlined"
            placeholder="Buscar subcategorías..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ bgcolor: 'white', borderRadius: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>
      </AppBar>
      <Container maxWidth={false} disableGutters>
        {loading || !userIdPet ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center">{error}</Typography>
        ) : subCategorias.length > 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenModal()}
                >
                  Nueva SubCategoría
                </Button>
              </Box>
              <TableContainer
                component={Paper}
                sx={{
                  width: '100%',
                  overflowX: 'auto',
                  boxShadow: 3,
                }}
              >
                <Table
                  sx={{
                    tableLayout: 'auto',
                  }}
                  aria-label="tabla de subcategorías"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Nombre SubCategoría
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Categoría
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Veterinaria
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Habilitada
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Creado
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Actualizado
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Acciones
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedSubCategorias.map((row) => {
                      if (!row || !row.id_sub_categoria) {
                        console.warn('Fila inválida en la tabla:', row);
                        return null;
                      }
                      return (
                        <TableRow
                          key={row.id_sub_categoria}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {row.id_sub_categoria}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 }, fontWeight: 'bold' }}>
                            {row.nombre_sub_categoria}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {categorias.find(c => c.id_categoria === row.id_categoria)?.nombre_categoria || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {pets.find(p => p.id_pet === row.id_pet)?.nombre_veterinaria || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            <Switch
                              checked={row.habilitada === true || row.habilitada === false ? row.habilitada : false}
                              onChange={() => handleToggleHabilitada(row.id_sub_categoria, row.habilitada ?? false)}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {formatDate(row.createdAt)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {formatDate(row.updatedAt)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            <IconButton onClick={() => handleOpenModal(row)} color="primary">
                              <EditIcon />
                            </IconButton>
                            <IconButton onClick={() => handleDelete(row.id_sub_categoria)} color="error">
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 2 }}>
                <Button
                  variant="contained"
                  disabled={page === 0}
                  onClick={() => handleChangePage(page - 1)}
                >
                  Anterior
                </Button>
                <Typography sx={{ alignSelf: 'center' }}>
                  Página {page + 1} de {Math.ceil(subCategorias.length / rowsPerPage)}
                </Typography>
                <Button
                  variant="contained"
                  disabled={page >= Math.ceil(subCategorias.length / rowsPerPage) - 1}
                  onClick={() => handleChangePage(page + 1)}
                >
                  Siguiente
                </Button>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6">Sin datos</Typography>
            <Typography color="text.secondary">
              No se encontraron subcategorías. Crea una nueva subcategoría para comenzar.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
              sx={{ mt: 2 }}
            >
              Nueva SubCategoría
            </Button>
          </Box>
        )}
        <Dialog open={openModal} onClose={() => setOpenModal(false)}>
          <DialogTitle>{selectedSubCategoria ? 'Editar SubCategoría' : 'Nueva SubCategoría'}</DialogTitle>
          <DialogContent>
            <TextField
              label="Nombre de la SubCategoría"
              fullWidth
              value={formData.nombre_sub_categoria}
              onChange={(e) => setFormData({ ...formData, nombre_sub_categoria: e.target.value })}
              sx={{ mt: 2 }}
              required
            />
            <TextField
              select
              label="Veterinaria"
              fullWidth
              value={formData.id_pet}
              onChange={(e) => setFormData({ ...formData, id_pet: e.target.value })}
              sx={{ mt: 2 }}
              required
              disabled
            >
              {pets.length > 0 ? (
                pets.map((pet) => (
                  <MenuItem key={pet.id_pet} value={pet.id_pet}>
                    {pet.nombre_veterinaria}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No hay veterinarias disponibles
                </MenuItem>
              )}
            </TextField>
            <TextField
              select
              label="Categoría"
              fullWidth
              value={formData.id_categoria}
              onChange={(e) => setFormData({ ...formData, id_categoria: e.target.value })}
              sx={{ mt: 2 }}
              required
            >
              {categorias.length > 0 ? (
                categorias.map((categoria) => (
                  <MenuItem key={categoria.id_categoria} value={categoria.id_categoria}>
                    {categoria.nombre_categoria}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No hay categorías disponibles
                </MenuItem>
              )}
            </TextField>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <Switch
                checked={formData.habilitada}
                onChange={(e) => setFormData({ ...formData, habilitada: e.target.checked })}
                color="primary"
              />
              <Typography>Habilitada</Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!formData.nombre_sub_categoria || !formData.id_pet || !formData.id_categoria}
            >
              Guardar
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ zIndex: 1500 }}
        >
          <Alert onClose={handleSnackbarClose} severity={error ? 'error' : 'success'} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default SubCategorias;